import React from 'react';
import ReactSelect, { components, OptionProps, SingleValueProps } from 'react-select';
import type { Props as SelectProps } from 'react-select';

interface OptionType {
  value: string | number;
  label: string;
  [key: string]: any;
}

interface CustomSelectProps extends SelectProps<OptionType> {
  label?: string;
  error?: string;
  containerClassName?: string;
  labelClassName?: string;
  errorClassName?: string;
}

const Select = React.forwardRef<any, CustomSelectProps>(({
  label,
  error,
  containerClassName = '',
  labelClassName = '',
  errorClassName = '',
  ...props
}, ref) => {
  const customStyles = {
    control: (base: any, state: any) => ({
      ...base,
      minHeight: '42px',
      backgroundColor: 'var(--card-bg)',
      borderColor: error 
        ? 'var(--danger)'
        : state.isFocused 
          ? 'var(--primary)' 
          : 'var(--border-color)',
      boxShadow: state.isFocused 
        ? '0 0 0 1px var(--primary)' 
        : 'none',
      '&:hover': {
        borderColor: state.isFocused 
          ? 'var(--primary)' 
          : 'var(--border-hover)',
      },
      borderRadius: '0.5rem',
      cursor: 'pointer',
    }),
    valueContainer: (base: any) => ({
      ...base,
      padding: '0 12px',
    }),
    input: (base: any) => ({
      ...base,
      color: 'var(--text-color)',
      margin: 0,
      padding: 0,
    }),
    singleValue: (base: any) => ({
      ...base,
      color: 'var(--text-color)',
    }),
    placeholder: (base: any) => ({
      ...base,
      color: 'var(--text-muted)',
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: 'var(--card-bg)',
      border: '1px solid var(--border-color)',
      borderRadius: '0.5rem',
      overflow: 'hidden',
      zIndex: 9999,
    }),
    menuList: (base: any) => ({
      ...base,
      padding: 0,
      maxHeight: '300px',
      '&::-webkit-scrollbar': {
        width: '6px',
      },
      '&::-webkit-scrollbar-track': {
        background: 'var(--border-color)',
      },
      '&::-webkit-scrollbar-thumb': {
        background: 'var(--text-muted)',
        borderRadius: '3px',
      },
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected
        ? 'var(--primary)'
        : state.isFocused
        ? 'var(--hover-bg)'
        : 'transparent',
      color: state.isSelected ? 'white' : 'var(--text-color)',
      cursor: 'pointer',
      '&:active': {
        backgroundColor: 'var(--primary)',
        color: 'white',
      },
      padding: '10px 16px',
    }),
    indicatorSeparator: (base: any) => ({
      ...base,
      backgroundColor: 'var(--border-color)',
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      color: 'var(--text-muted)',
      '&:hover': {
        color: 'var(--text-color)',
      },
    }),
    clearIndicator: (base: any) => ({
      ...base,
      color: 'var(--text-muted)',
      '&:hover': {
        color: 'var(--danger)',
      },
    }),
    multiValue: (base: any) => ({
      ...base,
      backgroundColor: 'var(--primary-100)',
    }),
    multiValueLabel: (base: any) => ({
      ...base,
      color: 'var(--primary)',
    }),
    multiValueRemove: (base: any) => ({
      ...base,
      color: 'var(--primary)',
      ':hover': {
        backgroundColor: 'var(--danger)',
        color: 'white',
      },
    }),
  };

  return (
    <div className={`w-full ${containerClassName}`}>
      {label && (
        <label 
          className={`block text-sm font-medium mb-1.5 text-gray-700 dark:text-gray-300 ${labelClassName}`}
        >
          {label}
          {props.required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <ReactSelect
        ref={ref}
        classNamePrefix="react-select"
        styles={customStyles}
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary: 'var(--primary)',
            primary25: 'var(--primary-50)',
            primary50: 'var(--primary-100)',
            primary75: 'var(--primary-200)',
            neutral0: 'var(--card-bg)',
            neutral5: 'var(--border-color)',
            neutral10: 'var(--border-color)',
            neutral20: 'var(--border-color)',
            neutral30: 'var(--border-hover)',
            neutral40: 'var(--text-muted)',
            neutral50: 'var(--text-muted)',
            neutral60: 'var(--text-color)',
            neutral70: 'var(--text-color)',
            neutral80: 'var(--text-color)',
            neutral90: 'var(--text-color)',
          },
        })}
        {...props}
      />

      {error && (
        <p className={`mt-1.5 text-sm text-red-600 dark:text-red-400 ${errorClassName}`}>
          {error}
        </p>
      )}
    </div>
  );
});

Select.displayName = 'Select';

export default Select;
